import React, { memo, useCallback, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
    Box,
    Divider,
    FormControlLabel,
    MenuItem,
    Tooltip,
    Typography,
} from '@mui/material'
import { AppMenu } from '../common/AppMenu'
import { ExtraSmallButton } from '../custom'
import { selectSyncSettings } from '../../selectors/widgetDashboard'
import { selectActiveLayout, selectActiveLayoutId } from '../../selectors/home'
import MultiPaneMenuIcon from '../../assets/icons/MultiPaneMenuIcon'
import { DashboardSyncSettings } from '../widgetDashboard/widgetDashboard.interface'
import { ShortcutsCategories, ShortcutType } from '../KeyboardShortcuts/Shortcuts.types'
import { MultiPaneMenuShortcutsKeyName } from '../KeyboardShortcuts/ShortcutsKeymap'
import useShortcuts from '../../hooks/useShortcuts'
import {
    updateSyncSettings,
} from '../../reducer/widgetDashboard.reducer'
import ToggleSwitch from '../common/toggleSwitch/toggleSwitch'
import PredefinedDashboardMenu from './predefinedPaneMenu'
import { selectMultiPaneFullScreen } from '../../selectors/common'
import { MULTIPANE_FULLSCREEN_MODE_ID } from '../../assets/constants'
import { ShortcutTooltipTitle, ShortcutKeys } from '../KeyboardShortcuts/ShortcutTooltipTitle'
import AnalyticsManager from '../../analytics/AnalyticsManager'
import { FeatureCategory, FeatureName, FeatureSubCategory } from '../adminUserRestrictions/Restrictions.constants'
import { useUser } from '../../context/UserContext'
export const syncSettingsMenuItems: Array<{
    label: string,
    key: keyof DashboardSyncSettings
}> = [{
    label: 'Symbol',
    key: 'security'
}, {
    label: 'Crosshair',
    key: 'crosshair'
}, {
    label: 'Interval',
    key: 'timeBucket'
}, {
    label: 'Time',
    key: 'time'
}, {
    label: 'Drawings',
    key: 'drawing'
}]

interface MultiPaneMenuProps {
    menuId?: string,
    disabled?: boolean
}

const MultiPaneMenu = ({
    menuId,
    disabled
}: MultiPaneMenuProps) => {
    const dispatch = useDispatch()
   const activeLayout = useSelector(selectActiveLayout)
    const activeLayoutId = useSelector(selectActiveLayoutId)
    const menuDisabled = disabled || !activeLayout
    const syncSettings = useSelector(selectSyncSettings(activeLayoutId))
    const buttonRef = useRef(null)
    const [anchorEl, setAnchorEl] = useState(null)
    const handleOpenPopover = useCallback((event: any) => {
        setAnchorEl(event.currentTarget)
    }, [])

    const handleClosePopover = useCallback(() => {
        setAnchorEl(null)
    }, [])

    const open = useMemo(() => Boolean(anchorEl), [anchorEl])

    const chunkedSettingsMenuItems = useMemo(() => syncSettingsMenuItems.reduce((chunks, item, index) => {
        const chunkIndex = Math.floor(index / 2)

        if (chunks.length <= chunkIndex) {
            chunks.push([])
        }

        chunks[chunkIndex].push(item)

        return chunks
    }, [] as Array<{
        label: string,
        key: keyof DashboardSyncSettings
    }[]>), [])

    const handleSwitchChange = (key: keyof DashboardSyncSettings) => {
        dispatch(updateSyncSettings({
            id: activeLayoutId,
            syncSettings: {
                ...syncSettings,
                [key]: !syncSettings[key]
            }
        }))
    }

    const handleButtonClick = useCallback(() => {
        AnalyticsManager.logEvent('multipane-menu menu opened', { source: 'keyboard' })
        if (buttonRef.current) {
            setAnchorEl(buttonRef.current)
        }
    }, [])

    const shortcuts: ShortcutType = useMemo(() => ({
        [MultiPaneMenuShortcutsKeyName.openMultiPaneMenu]: handleButtonClick,
    }), [handleButtonClick])

    useShortcuts(ShortcutsCategories.MULTI_PANE_MENU, shortcuts)

    const handleKeyDown = (event: any) => {
        const { key } = event
        const items = document.querySelectorAll('.menu-item')
        let index = Array.prototype.indexOf.call(items, document.activeElement)

        if (key === 'ArrowDown') {
            index = (index + 1) % items.length;
            (items[index] as HTMLElement).focus()
        } else if (key === 'ArrowUp') {
            index = (index - 1 + items.length) % items.length;
            (items[index] as HTMLElement).focus()
        }
    }
    const container = useSelector(selectMultiPaneFullScreen)
    return (
        <Box sx={{ height: 32, display: 'inline-flex', alignItems: 'center' }}>
            <Tooltip title={
                <ShortcutTooltipTitle label="Layout Setup">
                    <ShortcutKeys keys={['.']} />
                </ShortcutTooltipTitle>
            } arrow placement='bottom'>
                <span style={{ display: 'inline-block' }}>
                    <ExtraSmallButton
                        id={'multipane-menu-open-button'}
                        aria-label="Layout Setup"
                        size="small"
                        ref={buttonRef}
                        color="inherit"
                        onClick={handleOpenPopover}
                        disabled={menuDisabled}
                        data-event={'Multipane Button Clicked'}
                        data-event-source={'stockbar'}
                    >
                        <MultiPaneMenuIcon />
                    </ExtraSmallButton>
                </span>
            </Tooltip>
            {open && <AppMenu
                open={open}
                anchorEl={anchorEl}
                clickAwayIgnoreRefs={[buttonRef]}
                container={container ? document.getElementById(MULTIPANE_FULLSCREEN_MODE_ID) : null}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center'
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center'
                }}
                onClose={handleClosePopover}
                onKeyDown={handleKeyDown}
                slotProps={{
                    paper: {
                        id: menuId
                    }
                }}
            >
                <Box id={'multipane-menu-layout'} sx={{ display: 'flex', flexDirection: 'row' }}>
                    <Box sx={{ flex: 1 }}>
                        <PredefinedDashboardMenu />
                        <Divider sx={{ margin: 0 }} />
                        <Typography variant={'caption'} color="textSecondary" sx={{ p: '6px 16px' }}>SYNC IN LAYOUT</Typography>
                        {chunkedSettingsMenuItems.map((chunk, i) => (
                            <React.Fragment key={`${chunk[0].key}-${i}`}>
                                {chunk.map(({ label, key }) => (
                                    <MenuItem
                                        key={key}
                                        sx={{
                                            padding: 0
                                        }}
                                    >
                                        <FormControlLabel
                                            value={key}
                                            onChange={() => handleSwitchChange(key)}
                                            disableTypography
                                            control={
                                                <ToggleSwitch
                                                    size={'small'}
                                                    checked={syncSettings[key]}
                                                    disableRipple
                                                    data-event-source={'multipane'}
                                                    data-event-toggle={`${syncSettings[key] ? 'OFF' : 'ON'}`}
                                                    data-event={`${label} Sync`}
                                                />
                                            }
                                            sx={{
                                                padding: '4px 16px',
                                                 margin: 0,
                                                 width: '100%'
                                                }}
                                            labelPlacement={'start'}
                                            label={
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        width: '100%',
                                                        opacity: 0.9
                                                    }}
                                                >
                                                    {label}
                                                </Typography>
                                            }
                                        />
                                    </MenuItem>
                                ))}
                            </React.Fragment>
                        ))}
                    </Box>
                </Box>
            </AppMenu>}
        </Box>
    )
}
export default memo(MultiPaneMenu)
